// frontend\src\components\PurchaseOrders.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import PurchaseOrderDialog from './PurchaseOrderDialog';
import LoadingSpinner from './LoadingSpinner';
import { useTableSort, SortableTh } from '../hooks/useTableSort';
import '../core.css';
import '../purchaseOrders.css';

interface PurchaseOrder {
  id: number;
  po_number?: string;
  supplier: string;
  order_items: string;
  requested_date: string;
  estimated_date: string | null;
  ship_to_address: string;
  notes: string;
  status: string;
  last_updated: string;
}

interface PORow extends PurchaseOrder {
  display_number: string;
  items_count: number;
  overdue: boolean;
}

const parseOrderItems = (orderItems: string) => {
  try {
    // Format: "product_id-variation_id|product_name:quantity,..."
    return orderItems.split(',').map((item) => {
      const [productInfo, qty] = item.split(':');
      if (productInfo && productInfo.includes('|')) {
        const [, productName] = productInfo.split('|');
        return { name: productName.trim(), quantity: qty };
      }
      return { name: productInfo.trim(), quantity: qty };
    });
  } catch {
    return [{ name: orderItems, quantity: '1' }];
  }
};

const formatDate = (dateStr: string | null) =>
  dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const isOpenStatus = (status: string) =>
  !/received|cancel/i.test(status);

const PurchaseOrders: React.FC = () => {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [hideCompleted, setHideCompleted] = useState(true);

  useEffect(() => {
    fetchPurchaseOrders();
  }, []);

  const location = useLocation();
  const navigate = useNavigate();

  // Deep link from a task: /purchase-orders?po=ID opens that PO.
  useEffect(() => {
    const id = Number(new URLSearchParams(location.search).get('po'));
    if (!id || loading) return;
    const match = purchaseOrders.find((po) => po.id === id);
    if (match) {
      setCreatingNew(false);
      setSelectedPO(match);
      setDialogOpen(true);
    }
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, loading, purchaseOrders, navigate]);

  const fetchPurchaseOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get('/purchase-orders');
      setPurchaseOrders(response.data.purchaseOrders);
      setError(null);
    } catch (err: any) {
      console.error('Fetch purchase orders error:', err);
      setError('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const rows: PORow[] = useMemo(
    () =>
      purchaseOrders
        .filter((po) => !hideCompleted || !/received/i.test(po.status))
        .map((po) => ({
        ...po,
        display_number: po.po_number || `PO-${po.id}`,
        items_count: po.order_items ? parseOrderItems(po.order_items).length : 0,
          overdue:
            isOpenStatus(po.status) &&
            !!po.estimated_date &&
            new Date(po.estimated_date).getTime() < Date.now(),
        })),
    [purchaseOrders, hideCompleted]
  );

  // Default: newest first by creation (id mirrors creation order)
  const sort = useTableSort<PORow>(rows, 'id', 'desc');

  const handleCreatePO = () => {
    setSelectedPO(null);
    setCreatingNew(true);
    setDialogOpen(true);
  };

  const handleEditPO = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setCreatingNew(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPO(null);
    setCreatingNew(false);
    fetchPurchaseOrders(); // Refresh the list
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="stock-table-container">
        <div className="dash-error">
          {error} <button className="action-button" onClick={fetchPurchaseOrders}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="stock-table-container">
      <h1>Purchase Orders</h1>
      <div className="pull-data-container">
        <button onClick={handleCreatePO} className="action-button">
          Create Purchase Order
        </button>
      </div>
      <div className="filter-container">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={hideCompleted}
            onChange={(e) => setHideCompleted(e.target.checked)}
          />
          Hide Completed POs
        </label>
      </div>

      <>
        {rows.length === 0 ? (
          <p className="po-empty">
            {hideCompleted && purchaseOrders.length > 0
              ? 'No open purchase orders — uncheck "Hide Completed POs" to see received ones.'
              : 'No purchase orders yet. Create one to get started.'}
          </p>
        ) : (
          <table className="po-table">
            <thead>
              <tr>
                <SortableTh label="PO #" column="display_number" sort={sort} />
                <SortableTh label="Supplier" column="supplier" sort={sort} />
                <th>Items</th>
                <SortableTh label="Created" column="id" sort={sort} />
                <SortableTh label="Expected" column="estimated_date" sort={sort} />
                <SortableTh label="Status" column="status" sort={sort} />
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((po) => (
                <tr key={po.id} onClick={() => handleEditPO(po)} title="Open purchase order">
                  <td className="po-number-cell">{po.display_number}</td>
                  <td>{po.supplier}</td>
                  <td className="po-items-cell">
                    {parseOrderItems(po.order_items).map((item, index) => (
                      <div key={index} className="po-item-line">
                        <span className="po-item-qty">{item.quantity}×</span> {item.name}
                      </div>
                    ))}
                  </td>
                  <td>{formatDate(po.requested_date)}</td>
                  <td>
                    <span className={po.overdue ? 'po-overdue' : ''}>
                      {formatDate(po.estimated_date)}
                      {po.overdue ? ' (overdue)' : ''}
                    </span>
                  </td>
                  <td>
                    <span className={`status-chip status-${po.status.toLowerCase().replace(/\s+/g, '-')}`}>
                      {po.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>

      {dialogOpen && (
        <PurchaseOrderDialog
          purchaseOrder={selectedPO}
          isNew={creatingNew}
          onClose={handleCloseDialog}
        />
      )}
    </div>
  );
};

export default PurchaseOrders;
